import dropRight from 'lodash/dropRight';
import last from 'lodash/last';
import { Map as YMap, Array as YArray } from 'yjs';
import type { Doc as YDoc } from 'yjs';

export type ContentObjectType = YMap<unknown>;

export function getRootMap<T = unknown>(document: YDoc): YMap<T> {
  return document.getMap('root');
}

export function validateYMap<T = unknown>(toCheck?: unknown): YMap<T> {
  if (!(toCheck instanceof YMap)) {
    throw new Error('Object is not a YMap.');
  }
  return toCheck;
}

export function validateYArray<T = unknown>(toCheck?: unknown): YArray<T> {
  if (!(toCheck instanceof YArray)) {
    throw new Error('Object is not a YArray.');
  }
  return toCheck;
}

export function getRootChildrenArray(document: YDoc): YArray<string> {
  return validateYArray<string>(document.getMap('root').get('children'));
}

export function getContentObjectsMap(document: YDoc): YMap<ContentObjectType> {
  return validateYMap(document.getMap('root').get('contentObjects'));
}

export function getFormDataMap(document: YDoc): YMap<ContentObjectType> {
  return validateYMap(document.getMap('root').get('widgetsFormData'));
}

// Docs restored from pre-__yjsTypeHints staticContent reconstruct `errors` as a Y.Map instead of
// a plain object; call this right after deserializeYDoc so it never persists into the draft.
export function normalizeWidgetsFormDataErrors(document: YDoc): void {
  const formDataMap = document.getMap('root').get('widgetsFormData');
  if (!(formDataMap instanceof YMap)) return;

  (formDataMap as YMap<ContentObjectType>).forEach(widgetFormData => {
    if (!(widgetFormData.get('errors') instanceof YMap)) return;
    widgetFormData.set('errors', {});
  });
}

export function getSensitiveDataMap(document: YDoc): YMap<ContentObjectType> {
  return validateYMap(document.getMap('root').get('sensitiveData'));
}

export function getWidgetCustomCSSClassesMap(document: YDoc): YMap<string[]> {
  return validateYMap(document.getMap('root').get('widgetsCustomCSSClasses'));
}

export function findPositionInArray<T = unknown>(array: YArray<T>, item: T) {
  return array.toArray().findIndex(element => element === item);
}

type SensitiveDataMap = YMap<YMap<unknown>>;
type ContentObjectsMap = YMap<YMap<unknown>>;
type FormDataMap = YMap<YMap<unknown>>;

export function extractYjsMaps(document: YDoc, id: string) {
  const sensitiveDataMap = getSensitiveDataMap(document)?.get(id) as SensitiveDataMap;
  const contentObjectsMap = getContentObjectsMap(document)?.get(id) as ContentObjectsMap;
  const formDataMap = getFormDataMap(document)?.get(id) as FormDataMap;

  return { sensitiveDataMap, contentObjectsMap, formDataMap };
}

export function getNestedMap<T>(propertyPath: string, data: YMap<unknown> | undefined) {
  // Utility function to extract deeply nested properties
  // Example: answers.0.image.src for SelectionQuestion would look up root.contentObjects.<id>.answers.0.image.src
  // const { data } = useContentObject(parentMap);
  const propertyParts = propertyPath.split('.');
  const observedPropertyName = last(propertyParts) as string;
  const pathParts = dropRight(propertyParts);
  const nestedData: YMap<T> | undefined = pathParts.reduce(
    (data, pathPart) => data?.get(pathPart),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data as YMap<any> | undefined
  );
  if (!nestedData) {
    throw new Error(`Nested map at property path ${propertyPath} not found.`);
  }
  return [observedPropertyName, nestedData] as const;
}

/**
 * Safely converts a Buffer or Uint8Array to a Uint8Array, preserving offset and length.
 * This is critical when the buffer may have a byteOffset (e.g., from sliced buffers).
 *
 * @example
 * const buffer = Buffer.from([1, 2, 3, 4]);
 * const uint8 = bufferToUint8Array(buffer);
 */
export function bufferToUint8Array(buffer: Buffer | Uint8Array): Uint8Array {
  if (buffer instanceof Uint8Array && !(buffer instanceof Buffer)) {
    return buffer;
  }
  return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
}
